import type { TranslateFn } from "../../i18n";

/**
 * Mobile-only body shape question component. Renders a single question
 * (chest / midsection / seat / hips) as a vertical stack of full-width
 * cards matching the reference design:
 *
 *   STEP 04 / 06
 *
 *   WHICH BEST DESCRIBES
 *   YOUR MIDSECTION?
 *
 *   ┌────────────────────────────┐
 *   │ PROFILE A                  │
 *   │ FLAT                  [img]│
 *   └────────────────────────────┘
 *   ┌────────────────────────────┐
 *   │ PROFILE B           [DARK] │  ← selected (accent fill)
 *   │ AVERAGE              [img] │
 *   └────────────────────────────┘
 *   ┌────────────────────────────┐
 *   │ PROFILE C                  │
 *   │ FULL                 [img] │
 *   └────────────────────────────┘
 */

export interface BodyShapeOption<T extends string> {
  value: T;
  label: string;
  img?: string | null;
}

interface BodyShapeStepMobileProps<T extends string> {
  stepNumber: number;
  totalSteps: number;
  title: string;
  options: BodyShapeOption<T>[];
  selected: T | null;
  onSelect: (v: T) => void;
  t: TranslateFn;
}

export function BodyShapeStepMobile<T extends string>({
  stepNumber, totalSteps, title, options, selected, onSelect, t,
}: BodyShapeStepMobileProps<T>) {
  return (
    <div className="ps-bsm-root">
      {/* Step indicator */}
      <div className="ps-bsm-step">
        {t("STEP")} {String(stepNumber).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
      </div>

      {/* Big bold question */}
      <h2 className="ps-bsm-question">{title}</h2>

      {/* Stacked option cards — vertically centered in the remaining space */}
      <div className="ps-bsm-options-center">
      <div className="ps-bsm-options">
        {options.map((opt, idx) => {
          const isActive = selected === opt.value;
          const profileLetter = String.fromCharCode(65 + idx); // A, B, C, ...
          return (
            <button
              key={opt.value}
              type="button"
              className={`ps-bsm-card${isActive ? " ps-active" : ""}`}
              onClick={() => onSelect(opt.value)}
            >
              <div className="ps-bsm-card-content">
                <span className="ps-bsm-card-eyebrow">
                  {t("PROFILE")} {profileLetter}
                </span>
                <span className="ps-bsm-card-label">{opt.label.toUpperCase()}</span>
              </div>
              {opt.img && (
                <div className="ps-bsm-card-thumb">
                  <img src={opt.img} alt={opt.label} />
                </div>
              )}
            </button>
          );
        })}
      </div>
      </div>
    </div>
  );
}
